import HttpsRoundedIcon from '@mui/icons-material/HttpsRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import { Alert, Box, Card, Container, Link, Typography } from '@mui/material';
import { passwordStrength } from 'check-password-strength';
import { useFormik } from 'formik';
import LogRocket from 'logrocket';
import { useContext, useEffect, useMemo } from 'react';
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { bool, object, ObjectSchema, ref, string } from 'yup';
import AuthApi from '../../apis/AuthApi';
import FormikCheckbox from '../../components/FormikCheckbox/FormikCheckbox';
import FormikTextField from '../../components/FormikTextField/FormikTextField';
import SnackbarContext from '../../contexts/SnackbarContext';
import { CreateAccountDto } from '../../generated-types/createAccountDto';
import useApi from '../../lib/useApi';
import useFooterControl from '../../lib/useFooterControl';
import useSharedAuthLib from '../../lib/useSharedAuthLib';

/**
 * A dto that's modified from what the backend expects. This is used explicitly with formik.
 */
export interface CreateAccountWithConfirmDto extends CreateAccountDto {
  confirmPassword: string;
}

/**
 * A view for signing up the user
 */
const SignupView = () => {
  const schema: ObjectSchema<CreateAccountWithConfirmDto> = object({
    email: string().defined('Required').email('Email must be valid'),
    password: string()
      .defined('Required')
      .test(
        'password-strength',
        'Password must contain a lowercase letter, uppercase letter, number, special symbol, and be at least 8 characters long.',
        (password) => passwordStrength(password).id >= 2
      ),
    confirmPassword: string()
      .defined('Required')
      .oneOf([ref('password')], 'Passwords do not match'),
    firstName: string().defined('Required'),
    lastName: string().defined('Required'),
    agreedToPolicyAndTerms: bool().oneOf(
      [true],
      'You must agree to our Terms of Use and Privacy Policy.'
    ),
    publicClientOnboarding: bool().defined('Required'),
  });

  const authApi = useApi(AuthApi);
  const snackbarContext = useContext(SnackbarContext);
  const authLib = useSharedAuthLib();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const emailPreset = useMemo(() => params.get('email'), [params]);
  const firstNamePreset = useMemo(() => params.get('firstName'), [params]);
  const lastNamePreset = useMemo(() => params.get('lastName'), [params]);
  const onboardingPreset = useMemo(
    () => params.get('onboarding') != null,
    [params]
  );
  useFooterControl(true, false);

  const formik = useFormik<CreateAccountWithConfirmDto>({
    initialValues: {
      email: emailPreset ?? '',
      password: '',
      confirmPassword: '',
      firstName: firstNamePreset ?? '',
      lastName: lastNamePreset ?? '',
      agreedToPolicyAndTerms: false,
      publicClientOnboarding: onboardingPreset,
    },
    validationSchema: schema,
    onSubmit: (val) => {
      // Record the user trying to sign up
      LogRocket.identify(val.email);

      // Sign up the user
      authApi
        .CreateAccount(val)
        .then((response) => {
          switch (response.code) {
            case 201:
              snackbarContext.setAlert(
                <Alert variant="filled" severity="success">
                  Successfully created your account!
                </Alert>
              );

              // Set the authentication - the response token will always be populated if we get a 201
              if (response.token) authLib.SetToken(response.token);
              break;
            case 409:
              snackbarContext.setAlert(
                <Alert variant="filled" severity="error">
                  An account with that email already exists. Did you mean to{' '}
                  <Link
                    to="/login"
                    component={RouterLink}
                    style={{ textDecoration: 'underline', color: 'white' }}
                  >
                    log in
                  </Link>
                  ?
                </Alert>
              );
              formik.setSubmitting(false);
              break;
            default:
              throw Error();
          }
        })
        .catch(() => {
          snackbarContext.setAlert(
            <Alert variant="filled" severity="error">
              An unrecognized error occurred while creating your account. Please
              try again.
            </Alert>
          );
          formik.setSubmitting(false);
        })
        .finally(() => {
          snackbarContext.triggerSnackbar();
        });
    },
  });

  useEffect(() => {
    // Redirect the user once they have officially been authenticated,
    //  which includes processing of the new token
    if (authLib.AuthState.Authenticated) {
      // Send the user to a page based on their role
      navigate(authLib.GetNavigateRouteBasedOnRole());
    }
  }, [authLib, authLib.AuthState.Authenticated, navigate]);

  return (
    <Box height="100%" width="100%">
      <Container maxWidth="sm" sx={{ paddingY: '1em' }}>
        <Card elevation={5} sx={{ padding: '2em' }}>
          <Typography variant="h1" fontSize="3em" gutterBottom>
            Welcome to S3G!
          </Typography>
          <Typography marginBottom="1em">
            Please sign up using the form below
            {onboardingPreset && ' to begin onboarding'}.
          </Typography>
          <form onSubmit={formik.handleSubmit}>
            <FormikTextField
              formik={formik}
              fullWidth
              label="Email"
              name="email"
              autoFocus={emailPreset != null}
              disabled={emailPreset != null}
              sx={{
                marginBottom: '1em',
              }}
            />
            <FormikTextField
              formik={formik}
              fullWidth
              label="Password"
              autoFocus={emailPreset != null}
              name="password"
              type="password"
              autoComplete="new-password"
              sx={{
                marginBottom: '1em',
              }}
            />
            <FormikTextField
              formik={formik}
              fullWidth
              label="Confirm Password"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              sx={{
                marginBottom: '1em',
              }}
            />
            <FormikTextField
              formik={formik}
              fullWidth
              label="First Name"
              name="firstName"
              autoComplete="given-name"
              sx={{
                marginBottom: '1em',
              }}
            />
            <FormikTextField
              formik={formik}
              fullWidth
              label="Last Name"
              name="lastName"
              autoComplete="family-name"
              sx={{
                marginBottom: '1em',
              }}
            />
            <FormikCheckbox
              formik={formik}
              required
              name="agreedToPolicyAndTerms"
              label={
                <>
                  I agree to the S3G Security&nbsp;
                  <Link
                    to="/terms-of-use"
                    component={RouterLink}
                    color="textPrimary"
                    target="_blank"
                  >
                    Terms of Use
                  </Link>
                  &nbsp;and&nbsp;
                  <Link
                    to="/privacy-policy"
                    component={RouterLink}
                    color="textPrimary"
                    target="_blank"
                  >
                    Privacy Policy
                  </Link>
                </>
              }
            />
            <LoadingButton
              loading={formik.isSubmitting}
              loadingPosition="end"
              type="submit"
              variant="contained"
              endIcon={<HttpsRoundedIcon />}
            >
              Signup
            </LoadingButton>
          </form>
        </Card>
      </Container>
    </Box>
  );
};

export default SignupView;
